import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatTimeAgo } from '../utils/formatters';

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function fetchCount() {
    try {
      const { data } = await api.get('/api/notifications/unread-count');
      setUnreadCount(data.count);
    } catch {}
  }

  async function fetchNotifications() {
    try {
      const { data } = await api.get('/api/notifications?limit=20');
      setNotifications(data.notifications);
    } catch {}
  }

  function toggle() {
    if (!open) fetchNotifications();
    setOpen(!open);
  }

  async function markRead(id) {
    await api.put(`/api/notifications/${id}/read`);
    setNotifications(n => n.map(x => x.id === id ? { ...x, is_read: 1 } : x));
    setUnreadCount(c => Math.max(0, c - 1));
  }

  async function markAllRead() {
    await api.put('/api/notifications/read-all');
    setNotifications(n => n.map(x => ({ ...x, is_read: 1 })));
    setUnreadCount(0);
  }

  function handleClick(notif) {
    if (!notif.is_read) markRead(notif.id);
    if (notif.link) { navigate(notif.link); setOpen(false); }
  }

  const typeIcons = {
    mention: '@',
    task_assigned: 'T',
    task_completed: 'C',
    threshold_alert: '!',
    sync_failed: 'X',
    token_expired: 'K',
    backup_failed: 'B'
  };

  return (
    <div className="notification-bell" ref={ref}>
      <button onClick={toggle} aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open} aria-haspopup="true"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
        <svg viewBox="0 0 24 24"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
        {unreadCount > 0 && <span className="notification-badge" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <strong>Notifications</strong>
            {unreadCount > 0 && <button className="btn-link" onClick={markAllRead} style={{color:'var(--accent)',fontSize:'12px'}}>Mark all read</button>}
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-light)' }}>No notifications</div>
          ) : (
            notifications.map(n => (
              <div key={n.id} className={`notification-item ${!n.is_read ? 'unread' : ''}`} onClick={() => handleClick(n)}>
                <div className="n-title">{n.title}</div>
                <div className="n-desc">{n.description}</div>
                <div className="n-time">{formatTimeAgo(n.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
